import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowUpRightFromSquare,
  faLeaf,
  faSeedling,
  faFlask,
  faCloudSun,
  faLocationDot,
  faChartLine,
  faWheatAwn,
  faLanguage,
  faStore,
  faDroplet,
  faSatelliteDish,
} from "@fortawesome/free-solid-svg-icons";

import "./Services.css";
import WeatherApp from "../../components/WeatherApp";

const fieldImage = "/Images/field.webp";

const steps = [
  {
    number: "1",
    title: "Map your farmland",
    text: "Drop a pin or draw your boundary. We match your plot to our satellite grid within minutes.",
  },
  {
    number: "2",
    title: "We read the field",
    text: "Every satellite pass is processed for crop health, soil moisture, water stress and weather.",
  },
  {
    number: "3",
    title: "Get plain advice",
    text: "Clear recommendations arrive in your regional language, by app and by SMS.",
  },
  {
    number: "4",
    title: "Act and verify",
    text: "Adjust irrigation or inputs, then use the next pass to confirm whether the field responded.",
  },
];

const intelligenceCards = [
  {
    icon: "⌁",
    tag: "LIVE",
    title: "Crop Health Monitoring",
    text: "Track crop health using satellite and drone intelligence to identify disease, pest stress and nutrient deficiencies early.",
  },
  {
    icon: "⌖",
    tag: "FIELD",
    title: "Geo Location",
    text: "Get precise field-level insights and recommendations based on your farm location, boundaries and crop conditions.",
  },
  {
    icon: "☁",
    tag: "7 DAYS",
    title: "Weather Forecast",
    text: "Plan irrigation, spraying and harvesting with accurate weather forecasts designed around your farm.",
  },
  {
    icon: "✳",
    tag: "LOCAL",
    title: "Hyperlocal Weather",
    text: "Understand weather conditions at the farm level with localized rainfall, temperature and wind intelligence.",
  },
  {
    icon: "≋",
    tag: "ALERT",
    title: "Water Stress",
    text: "Identify water-stressed areas early and take corrective action before crop stress becomes visible.",
  },
  {
    icon: "⚗",
    tag: "EARLY ACTION",
    title: "Crop Clinic",
    text: "Identify crop problems faster with technology-assisted diagnosis. Understand symptoms, possible causes and practical next steps before an issue spreads across the field.",
  },
  {
    icon: "♧",
    tag: "SOIL INSIGHT",
    title: "Soil Health",
    text: "Understand soil conditions, nutrients and organic carbon so you can plan inputs more carefully, improve soil health and support stronger crop performance.",
  },
  {
    icon: "⌁",
    tag: "FIELD MONITORING",
    title: "Crop Health",
    text: "Monitor crop performance across your field and spot changes early with satellite intelligence, helping you respond before small problems become avoidable losses.",
  },
];

const solutions = [
  {
    number: "01",
    title: "Agri-input",
    description:
      "Practical input recommendations that support healthier soil, responsible cultivation and better cost management.",
    icon: faSeedling,
  },
  {
    number: "02",
    title: "Consultation",
    description:
      "Unbiased guidance that helps farmers understand crop problems and decide what action makes sense for their field.",
    icon: faFlask,
  },
  {
    number: "03",
    title: "Regional Advisory",
    description:
      "Useful crop and field recommendations in familiar regional languages, designed to be understood and applied easily.",
    icon: faLanguage,
  },
  {
    number: "04",
    title: "Market Access",
    description:
      "Better market signals and direct opportunities help farmers plan sales and work toward stronger returns from their produce.",
    icon: faStore,
  },
];

const Services = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <main className="farmology-services-page">
      {/* =========================================================
          SECONDARY PAGE HERO
      ========================================================== */}
      <section className="services-page-hero">
        <img
          src="/Images/About_Header.webp"
          alt="Farmology agricultural services"
          className="services-hero-image"
        />
        <div className="services-hero-overlay" />

        <div className="services-container services-page-hero-inner">
          <div className="services-page-hero-copy">
            <div className="services-page-hero-eyebrow">
              <span /> FARMOLOGY SERVICES
            </div>

            <h1>
              Smarter tools.
              <br />
              <span>Better farming.</span>
            </h1>

            <p>
              Technology-driven solutions designed to help farmers make better
              decisions, improve productivity and build a more sustainable
              future.
            </p>

            <div className="services-breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Services</span>
            </div>
          </div>
        </div>

        <div className="services-hero-number">01</div>

        <div className="services-hero-bottom">
          <span>FARMOLOGY</span>
          <div />
          <span>SMART AGRICULTURE</span>
        </div>
      </section>

      {/* =========================================================
          SERVICES - SAME CORE SYSTEM AS HOME PAGE
      ========================================================== */}
      <section id="services" className="services-intelligence section-grid">
        <div className="services-container">
          <div className="services-section-heading centered">
            <div className="services-eyebrow">OUR SERVICES</div>

            <h2>Field intelligence built around every farming decision</h2>

            <p>
              Farmology brings satellite, weather, location and field
              intelligence together so farmers can monitor conditions,
              understand risks and make better decisions at the right time.
            </p>
          </div>

          <div className="services-intelligence-grid">
            {intelligenceCards.map((card) => (
              <article className="services-intel-card" key={card.title}>
                <div className="services-intel-top">
                  <span className="services-intel-icon">{card.icon}</span>
                  <span className="services-intel-tag">{card.tag}</span>
                </div>

                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          HOW IT WORKS — SAME CONTENT AS HOME PAGE
      ========================================================== */}
      <section id="how-it-works" className="services-how section">
        <div className="services-container services-how-grid">
          <div className="services-how-visual">
            <div
              className="services-how-image"
              style={{ "--field-image": `url("${fieldImage}")` }}
            >
              <div className="services-how-overlay" />

              <div className="services-how-badge">
                <span /> SATELLITE ANALYSIS
              </div>

              <div className="services-field-card">
                <span className="services-field-icon">
                  <FontAwesomeIcon icon={faLocationDot} />
                </span>
                <span>
                  <strong>Your farmland</strong>
                  <small>Field successfully mapped</small>
                </span>
              </div>
            </div>
          </div>

          <div className="services-how-copy">
            <div className="services-eyebrow">HOW IT WORKS</div>

            <h2>From satellite data to practical action</h2>

            <p>
              Start with your farmland. Farmology combines available satellite,
              weather and field information, processes it into useful insights,
              and helps you decide what action to take next.
            </p>

            <div className="services-how-points">
              {steps.map((step, index) => (
                <button
                  type="button"
                  className={`services-how-step ${activeStep === index ? "active" : ""}`}
                  key={step.number}
                  onClick={() => setActiveStep(index)}
                >
                  <span className="services-how-step-number">
                    {step.number}
                  </span>
                  <span className="services-how-step-content">
                    <strong>{step.title}</strong>
                    <small>{step.text}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          SOLUTIONS
      ========================================================== */}
      <section className="services-solutions section">
        <div className="services-container">
          <div className="services-solutions-heading">
            <div>
              <div className="services-eyebrow">OUR SOLUTIONS</div>
              <h2>
                From farm
                <br />
                <span>to market.</span>
              </h2>
            </div>
            <p>
              Technology becomes valuable when it helps with the decisions that
              happen after the data. Farmology brings advisory, inputs, language
              support and market access into the same journey.
            </p>
          </div>

          <div className="services-solutions-grid">
            {solutions.map((solution) => (
              <article className="services-solution-card" key={solution.number}>
                <div className="services-solution-top">
                  <span>{solution.number}</span>
                  <div className="services-solution-icon">
                    <FontAwesomeIcon icon={solution.icon} />
                  </div>
                </div>
                <div className="services-solution-content">
                  <span>SOLUTION / {solution.number}</span>
                  <h3>{solution.title}</h3>
                  <p>{solution.description}</p>
                </div>
                <div className="services-solution-bottom">
                  <span>FARMOLOGY</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          WEATHER
      ========================================================== */}
      <section className="services-weather section-grid">
        <div className="services-container">
          <div className="services-section-heading centered services-weather-heading">
            <div className="services-eyebrow">REAL-TIME INFORMATION</div>
            <h2>
              Weather that works
              <br />
              <span>for farmers.</span>
            </h2>
            <p>
              Localized weather information helps farmers plan irrigation,
              spraying, harvesting and other field activities with better
              timing.
            </p>
          </div>

          <div className="services-weather-app">
            <div className="services-weather-header">
              <div>
                <span>FARMOLOGY WEATHER</span>
                <h3>Weather information</h3>
              </div>
              <div className="services-weather-location">
                <FontAwesomeIcon icon={faLocationDot} />
              </div>
            </div>
            <div className="services-weather-content">
              <WeatherApp />
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          CTA
      ========================================================== */}
      <section className="services-final-cta">
        <div className="services-container">
          <div className="services-cta-box">
            <div className="services-cta-copy">
              <div className="services-cta-eyebrow">START TODAY</div>
              <h2>Ready to make your next farm decision smarter?</h2>
              <p>
                Start by understanding your field better and discover how
                Farmology can help you monitor crop health, weather and water
                conditions.
              </p>
            </div>

            <div className="services-cta-actions">
              <Link to="/user/login" className="services-cta-light">
                Get Started
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/contact" className="services-cta-white">
                Connect with Us
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
